import React, { useState } from 'react';
import { Search, UserPlus, MapPin, Phone, Mail } from 'lucide-react';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const CustomerSelector = ({ customer, customerOptions, onChange }) => {
  const [showCustomerDetails, setShowCustomerDetails] = useState(false);
  const [showAddCustomer, setShowAddCustomer] = useState(false);

  const handleCustomerSelect = (value) => {
    const selectedCustomer = customerOptions?.find(opt => opt?.value === value);
    onChange(selectedCustomer);
    setShowCustomerDetails(!!selectedCustomer);
  };

  const formatCustomerOptions = customerOptions?.map(customer => ({
    value: customer?.value,
    label: customer?.label,
    description: customer?.email
  }));

  return (
    <div className="bg-white rounded-lg border p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-foreground">Customer Information</h2>
        <Button
          variant="outline"
          size="sm"
          iconName="UserPlus"
          iconPosition="left"
          onClick={() => setShowAddCustomer(true)}
        >
          Add New Customer
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Customer Selection */}
        <div className="space-y-4">
          <Select
            label="Select Customer"
            placeholder="Search and select customer..."
            options={formatCustomerOptions}
            value={customer?.value || ''}
            onChange={handleCustomerSelect}
            searchable={true}
            clearable={true}
            required
            className="w-full"
          />

          {customer && (
            <div className="space-y-3 p-4 bg-gray-50 rounded-md">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">{customer?.email}</span>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">{customer?.phone}</span>
              </div>
              <div className="flex items-start gap-2">
                <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                <span className="text-sm whitespace-pre-line">{customer?.address}</span>
              </div>
              {customer?.taxNumber && (
                <div className="text-sm">
                  <span className="font-medium">Tax ID: </span>
                  <span>{customer?.taxNumber}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Billing Address */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-foreground">Billing Address</h3>
          {customer ? (
            <div className="p-4 bg-gray-50 rounded-md">
              <p className="font-medium">{customer?.label}</p>
              <p className="text-sm text-muted-foreground whitespace-pre-line mt-1">
                {customer?.address}
              </p>
              <div className="mt-2 pt-2 border-t border-gray-200">
                <p className="text-sm">
                  <span className="font-medium">Email: </span>
                  {customer?.email}
                </p>
                <p className="text-sm">
                  <span className="font-medium">Phone: </span>
                  {customer?.phone}
                </p>
              </div>
            </div>
          ) : (
            <div className="p-4 bg-gray-50 rounded-md text-center">
              <p className="text-sm text-muted-foreground">
                Select a customer to see billing address
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Quick Customer Search */}
      <div className="mt-4 pt-4 border-t">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Search className="h-4 w-4" />
          <span>Recent customers: Acme Corporation, TechStart Inc., Global Solutions Ltd</span>
        </div>
      </div>

      {/* Add Customer Modal (Simple) */}
      {showAddCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-4">Add New Customer</h3>
            <div className="space-y-4">
              <Input label="Company Name" required />
              <Input label="Email Address" type="email" required />
              <Input label="Phone Number" />
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowAddCustomer(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  className="flex-1"
                  onClick={() => {
                    alert('Customer added successfully!');
                    setShowAddCustomer(false);
                  }}
                >
                  Add Customer
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerSelector;